<template>
  <div class="auth-page">
    <!-- Background decoration -->
    <div class="auth-bg">
      <div class="auth-bg-shape shape-1"></div>
      <div class="auth-bg-shape shape-2"></div>
      <div class="auth-bg-shape shape-3"></div>
    </div>

    <div class="auth-card">
      <!-- Lock icon -->
      <div class="lock-icon">
        <vue-feather type="shield" size="28" />
      </div>

      <div class="auth-header">
        <h1>Two-Factor Authentication</h1>
        <p>Enter the 6-digit code from your authenticator app</p>
      </div>

      <form @submit.prevent="handleVerify" class="auth-form">
        <div class="otp-container">
          <input
            v-for="(_, index) in otpDigits"
            :key="index"
            :ref="el => otpRefs[index] = el"
            v-model="otpDigits[index]"
            type="text"
            inputmode="numeric"
            maxlength="1"
            class="otp-input"
            :class="{ 'has-value': otpDigits[index], 'error': error }"
            @input="handleOtpInput(index)"
            @keydown="handleOtpKeydown($event, index)"
            @paste="handlePaste"
            @focus="error = ''"
          />
        </div>

        <Transition name="shake">
          <div v-if="error" class="error-message">
            <vue-feather type="alert-circle" size="16" />
            <span>{{ error }}</span>
          </div>
        </Transition>

        <button 
          type="submit" 
          class="submit-btn"
          :class="{ 'loading': loading }"
          :disabled="loading || !isOtpComplete"
        >
          <span v-if="loading" class="btn-loader"></span>
          <span class="btn-text">{{ loading ? 'Verifying...' : 'Verify' }}</span>
          <vue-feather v-if="!loading" type="arrow-right" size="18" class="btn-icon" />
        </button>

        <div class="auth-footer">
          <router-link to="/" class="back-link">
            <vue-feather type="arrow-left" size="16" />
            <span>Back to login</span>
          </router-link>
        </div>
      </form>

      <div class="help-text">
        <vue-feather type="help-circle" size="14" />
        <span>Open your authenticator app to view your code</span>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useNotifications } from '@/composables/useNotifications'

export default {
  name: 'TwoFactorAuth',
  setup() {
    const router = useRouter()
    const authStore = useAuthStore()
    const { toast } = useNotifications()

    const otpDigits = ref(['', '', '', '', '', ''])
    const otpRefs = ref([])
    const loading = ref(false)
    const error = ref('')

    const isOtpComplete = computed(() => otpDigits.value.every(d => d !== ''))
    const otpCode = computed(() => otpDigits.value.join(''))

    const handleOtpInput = (index) => {
      const value = otpDigits.value[index]
      if (value && index < 5) {
        otpRefs.value[index + 1]?.focus()
      }
    }

    const handleOtpKeydown = (event, index) => {
      if (event.key === 'Backspace' && !otpDigits.value[index] && index > 0) {
        otpRefs.value[index - 1]?.focus()
      }
    }

    const handlePaste = (event) => {
      event.preventDefault()
      const pastedData = event.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
      pastedData.split('').forEach((char, i) => {
        if (i < 6) otpDigits.value[i] = char
      })
    }

    const handleVerify = async () => {
      if (!isOtpComplete.value) return

      loading.value = true
      error.value = ''

      try {
        await authStore.verify2FA(otpCode.value)
        toast.success(`Welcome back, ${authStore.user?.fullName || 'Admin'}!`)
        router.push({ name: 'dashboard' })
      } catch (err) {
        error.value = typeof err === 'string' ? err : 'Invalid code. Please try again.'
        toast.error('Verification failed')
        otpDigits.value = ['', '', '', '', '', '']
        otpRefs.value[0]?.focus()
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      if (!authStore.pending2FA) {
        router.push({ name: 'login' })
        return
      }
      otpRefs.value[0]?.focus()
    })

    return {
      otpDigits, otpRefs, loading, error, isOtpComplete,
      handleOtpInput, handleOtpKeydown, handlePaste, handleVerify
    }
  }
}
</script>

<style scoped>
/* Base layout */
.auth-page {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  overflow: hidden;
  padding: 20px;
}

.auth-bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}

.auth-bg-shape {
  position: absolute;
  border-radius: 50%;
  opacity: 0.5;
}

.shape-1 {
  width: 600px; height: 600px;
  background: linear-gradient(135deg, rgba(0, 48, 73, 0.08) 0%, rgba(0, 48, 73, 0.02) 100%);
  top: -200px; right: -100px;
  animation: float 20s ease-in-out infinite;
}

.shape-2 {
  width: 400px; height: 400px;
  background: linear-gradient(135deg, rgba(252, 191, 73, 0.1) 0%, rgba(252, 191, 73, 0.02) 100%);
  bottom: -100px; left: -100px;
  animation: float 15s ease-in-out infinite reverse;
}

.shape-3 {
  width: 300px; height: 300px;
  background: linear-gradient(135deg, rgba(142, 202, 230, 0.1) 0%, rgba(142, 202, 230, 0.02) 100%);
  top: 50%; left: 30%;
  animation: float 18s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translate(0, 0) rotate(0deg); }
  25% { transform: translate(20px, -20px) rotate(5deg); }
  50% { transform: translate(-10px, 20px) rotate(-5deg); }
  75% { transform: translate(-20px, -10px) rotate(3deg); }
}

/* Card */
.auth-card {
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: 24px;
  padding: 48px 40px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 20px 25px -5px rgba(0, 0, 0, 0.05);
  position: relative;
  z-index: 1;
  text-align: center;
}

/* Lock icon */
.lock-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px; height: 64px;
  background: linear-gradient(135deg, #003049 0%, #073b4c 100%);
  border-radius: 20px;
  color: #fff;
  margin-bottom: 24px;
  box-shadow: 0 8px 24px rgba(0, 48, 73, 0.25);
}

/* Header */
.auth-header { margin-bottom: 32px; }
.auth-header h1 { font-size: 24px; font-weight: 700; color: #1f2937; margin-bottom: 8px; letter-spacing: -0.02em; }
.auth-header p { color: #6b7280; font-size: 14px; }

/* OTP Input */
.otp-container {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 24px;
}

.otp-input {
  width: 48px; height: 58px;
  border: 2px solid #e5e7eb;
  border-radius: 14px;
  font-size: 24px; font-weight: 700;
  text-align: center; color: #1f2937;
  background: #fff;
  transition: all 0.2s ease;
  outline: none;
}

.otp-input:focus {
  border-color: #003049;
  box-shadow: 0 0 0 4px rgba(0, 48, 73, 0.1);
}

.otp-input.has-value {
  border-color: #003049;
  background: #f8fafc;
}

.otp-input.error {
  border-color: #ef4444;
  animation: shake 0.5s ease;
}

/* Error message */
.error-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  color: #dc2626;
  font-size: 14px;
  margin-bottom: 20px;
}

.shake-enter-active { animation: shake 0.5s ease; }

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20%, 60% { transform: translateX(-8px); }
  40%, 80% { transform: translateX(8px); }
}

/* Submit button */
.submit-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 16px 24px;
  background: linear-gradient(135deg, #003049 0%, #073b4c 100%);
  color: #fff;
  border: none;
  border-radius: 14px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 48, 73, 0.35);
}

.submit-btn:active:not(:disabled) { transform: translateY(0); }
.submit-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.submit-btn.loading { pointer-events: none; }

.btn-loader {
  width: 18px; height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin { to { transform: rotate(360deg); } }

.btn-icon { transition: transform 0.2s ease; }
.submit-btn:hover:not(:disabled) .btn-icon { transform: translateX(4px); }

/* Footer */
.auth-footer { margin-top: 20px; }

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #6b7280;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  transition: color 0.2s ease;
}

.back-link:hover { color: #003049; }

/* Help text */
.help-text {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  font-size: 12px;
  color: #9ca3af;
}

/* Responsive */
@media (max-width: 480px) {
  .auth-card { padding: 32px 24px; }
  .otp-input { width: 42px; height: 52px; font-size: 20px; }
}

/* Dark mode */
:global(body.dark-mode) .auth-page { background: linear-gradient(135deg, #111827 0%, #1f2937 100%); }
:global(body.dark-mode) .auth-card { background: #1f2937; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2), 0 10px 15px -3px rgba(0, 0, 0, 0.3); }
:global(body.dark-mode) .lock-icon { background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); }
:global(body.dark-mode) .auth-header h1 { color: #f3f4f6; }
:global(body.dark-mode) .auth-header p { color: #9ca3af; }
:global(body.dark-mode) .otp-input { background: #374151; border-color: #4b5563; color: #f3f4f6; }
:global(body.dark-mode) .otp-input:focus { border-color: #3b82f6; box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.2); }
:global(body.dark-mode) .otp-input.has-value { background: #374151; border-color: #3b82f6; }
:global(body.dark-mode) .error-message { background: rgba(239, 68, 68, 0.1); border-color: rgba(239, 68, 68, 0.3); }
:global(body.dark-mode) .submit-btn { background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); }
:global(body.dark-mode) .back-link { color: #9ca3af; }
:global(body.dark-mode) .back-link:hover { color: #60a5fa; }
:global(body.dark-mode) .help-text { border-color: #374151; color: #6b7280; }
</style>
